export async function runConcurrentTasks<TItem, TResult>(input: {
  items: TItem[];
  concurrency: number;
  worker: (item: TItem, index: number) => Promise<TResult>;
  onItemStart?: (info: {
    item: TItem;
    index: number;
    total: number;
  }) => void | Promise<void>;
  onItemComplete?: (info: {
    item: TItem;
    index: number;
    result: TResult;
    done: number;
    total: number;
  }) => void | Promise<void>;
}): Promise<TResult[]> {
  const { items, worker, onItemStart, onItemComplete } = input;
  const total = items.length;
  if (total === 0) return [];

  const concurrency = Math.max(1, Math.min(input.concurrency, total));
  const results = new Array<TResult>(total);
  let nextIndex = 0;
  let done = 0;
  // 所有生命周期回调共享一条队列，避免 start 心跳和 complete 进度写并发覆盖。
  let hookQueue = Promise.resolve();

  function enqueueStartHook(info: { item: TItem; index: number; total: number }) {
    const current = hookQueue.then(() => onItemStart?.(info));
    hookQueue = current;
    return current;
  }

  function enqueueCompleteHook(info: {
    item: TItem;
    index: number;
    result: TResult;
    done: number;
    total: number;
  }) {
    const current = hookQueue.then(() => onItemComplete?.(info));
    hookQueue = current;
    return current;
  }

  async function runWorker() {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= total) return;

      const item = items[index]!;
      await enqueueStartHook({ item, index, total });
      const result = await worker(item, index);
      results[index] = result;
      done += 1;
      await enqueueCompleteHook({ item, index, result, done, total });
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => runWorker()));
  return results;
}
